import { useMap } from "@vis.gl/react-maplibre";
import type { MapMouseEvent } from "maplibre-gl";
import { useCallback, useEffect, useRef, useState } from "react";
import useEvent from "react-use-event-hook";

import { isControlClick } from "@ryde/shared";

import {
  LASSO_DUPLICATE_POINT_EPSILON_PX,
  LASSO_MIN_POINTS,
  LASSO_SNAP_DISTANCE_PX,
} from "./maplibre-lasso-select.constants";
import { getAliveMap } from "./maplibre-map.utils";
import {
  useMapLibreEndPlaceInfoOnToolPick,
  useMapLibreIsPickingPlace,
} from "./maplibre-place-info.context";
import type { IMapLibreTraceControls } from "./maplibre-polygon-editor.models";

interface IUseMapLibreLassoSelectArgs {
  isActive: boolean;
  /** Fired with the finished ring, as `[lat, lng]` pairs. The lasso is discarded straight after. */
  onComplete: (ring: [number, number][]) => void;
  /** Escape, or a finish with too few corners to enclose anything. */
  onCancel: () => void;
}

/**
 * Draw-a-shape-to-select gesture, in the same three ways Leaflet-geoman offered it: click to drop each
 * corner, and finish by double-clicking or by clicking back on the first corner. Escape abandons it.
 *
 * As in geoman, the cursor is magnetised onto the first corner once it comes within snapping distance, so
 * closing the ring is a gesture you can feel rather than a pixel you have to hit.
 *
 * The ring is never kept — it exists only to answer "which stations are inside", so the caller gets the
 * points and the lasso clears itself.
 *
 * Corners live in a ref, with state mirroring it purely for rendering. They cannot live in state alone:
 * the map handlers are plain DOM listeners that need the current ring synchronously, and reading it inside
 * a `setState` updater would mean finishing the shape — firing `onComplete`, clearing the points — from
 * within an updater, which has to stay pure. React would run it twice in StrictMode and select twice.
 */
export const useMapLibreLassoSelect = ({
  isActive,
  onComplete,
  onCancel,
}: IUseMapLibreLassoSelectArgs): IMapLibreTraceControls & {
  points: [number, number][];
  cursor: [number, number] | null;
} => {
  const { current: mapRef } = useMap();
  const isPickingPlace = useMapLibreIsPickingPlace();
  useMapLibreEndPlaceInfoOnToolPick(isActive ? "lasso" : undefined);
  const pointsRef = useRef<[number, number][]>([]);
  const [points, setPoints] = useState<[number, number][]>([]);
  const [cursor, setCursor] = useState<[number, number] | null>(null);

  const setRing = useCallback((ring: [number, number][]): void => {
    pointsRef.current = ring;
    setPoints(ring);
  }, []);

  const reset = useCallback((): void => {
    setRing([]);
    setCursor(null);
  }, [setRing]);

  const finish = useEvent((): void => {
    const ring = pointsRef.current;
    setRing([]);

    if (ring.length >= LASSO_MIN_POINTS) {
      onComplete(ring);
      return;
    }

    onCancel();
  });

  const cancel = useEvent((): void => {
    reset();
    onCancel();
  });

  /**
   * Geoman's Remove Last Vertex. Below two corners there is nothing left to walk back to, so it abandons
   * the trace outright rather than leaving a single orphaned corner on the map.
   */
  const removeLastVertex = useEvent((): void => {
    const ring = pointsRef.current;

    if (ring.length <= 1) {
      cancel();
      return;
    }

    setRing(ring.slice(0, -1));
  });

  useEffect(() => {
    if (!isActive) {
      reset();
    }
  }, [isActive, reset]);

  // Paused while place info owns the pointer, not reset: the corners stay, and only the rubber band goes.
  useEffect(() => {
    if (isPickingPlace) {
      setCursor(null);
    }
  }, [isPickingPlace]);

  useEffect(() => {
    const map = getAliveMap(mapRef);

    if (!map || !isActive || isPickingPlace) {
      return;
    }

    const canvas = map.getCanvas();

    const distanceToPx = (
      point: [number, number],
      event: MapMouseEvent,
    ): number => {
      const projected = map.project([point[1], point[0]]);
      const clicked = map.project(event.lngLat);

      return Math.hypot(projected.x - clicked.x, projected.y - clicked.y);
    };

    /**
     * Where the next click would actually land. Geoman magnetised the cursor onto the first corner once it
     * came within snapping distance and then committed that exact position, which is how a ring closed.
     * Only the first corner ever attracted — it is the only one geoman registered as a snap layer.
     */
    const resolveSnappedCursor = (
      event: MapMouseEvent,
    ): { position: [number, number]; isSnappedToFirst: boolean } => {
      const [first] = pointsRef.current;

      if (first && distanceToPx(first, event) <= LASSO_SNAP_DISTANCE_PX) {
        return { position: first, isSnappedToFirst: true };
      }

      return {
        position: [event.lngLat.lat, event.lngLat.lng],
        isSnappedToFirst: false,
      };
    };

    const handleClick = (event: MapMouseEvent): void => {
      // The control cluster sits over the canvas. React's stopPropagation cannot help here: React attaches
      // at the root, so the native event has already reached MapLibre's own listener by then.
      if (isControlClick(event)) {
        return;
      }

      const current = pointsRef.current;
      const { position, isSnappedToFirst } = resolveSnappedCursor(event);

      // Magnetised onto the first corner, the click closes the ring — no double-click needed, and so no
      // zoom either. With fewer than three corners geoman's _finishShape bailed and left the tool armed
      // rather than stacking another corner on the first one, so nothing happens here either.
      if (isSnappedToFirst) {
        if (current.length >= LASSO_MIN_POINTS) {
          finish();
        }

        return;
      }

      // A double-click arrives as click, click, dblclick — so without this the closing gesture would drop
      // a second corner on top of the one already there.
      const last = current.at(-1);

      if (
        last &&
        distanceToPx(last, event) <= LASSO_DUPLICATE_POINT_EPSILON_PX
      ) {
        return;
      }

      setRing([...current, position]);
    };

    const handleDoubleClick = (event: MapMouseEvent): void => {
      // Without this the map also zooms on the gesture that ends the shape.
      event.preventDefault();
      finish();
    };

    // Snapping here is what makes the magnet visible: the rubber band collapses onto the first corner
    // before the click, exactly as geoman's cursor marker jumped there.
    const handleMouseMove = (event: MapMouseEvent): void => {
      setCursor(resolveSnappedCursor(event).position);
    };

    const handleMouseOut = (): void => setCursor(null);

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        cancel();
      }
    };

    map.on("click", handleClick);
    map.on("dblclick", handleDoubleClick);
    map.on("mousemove", handleMouseMove);
    map.on("mouseout", handleMouseOut);
    document.addEventListener("keydown", handleKeyDown);
    canvas.style.cursor = "crosshair";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      const aliveMap = getAliveMap(mapRef);
      if (!aliveMap) {
        return;
      }

      aliveMap.off("click", handleClick);
      aliveMap.off("dblclick", handleDoubleClick);
      aliveMap.off("mousemove", handleMouseMove);
      aliveMap.off("mouseout", handleMouseOut);
      canvas.style.cursor = "";
    };
  }, [mapRef, isActive, isPickingPlace, finish, cancel, setRing]);

  return { points, cursor, finish, removeLastVertex, cancel };
};
